import React, { createContext, useCallback, useContext, useState } from "react";

export interface IBookingEnquirySnapshot {
  stage: string;
  branchId?: string;
  vehicleGroupId?: string;
  vehicleGroupName?: string;
  pickupDateTime?: string;
  dropoffDateTime?: string;
  rentalPrice?: number;
  insuranceName?: string;
  insurancePrice?: number;
  addonsPrice?: number;
  estimatedTotal?: number;
  bookingReferenceNumber?: string;
  addons?: string[];
}

interface IAddonRequirementLike {
  name: string;
  displayName?: string;
  quantity: number;
}

export function summarizeSelections(
  addonRequirements?: IAddonRequirementLike[],
  protectionPlanName?: string
): string[] {
  const items: string[] = [];
  if (protectionPlanName) {
    items.push(protectionPlanName);
  }
  addonRequirements?.forEach((addon) => {
    if (addon.quantity > 0) {
      const label = addon.displayName || addon.name;
      items.push(addon.quantity > 1 ? `${label} x${addon.quantity}` : label);
    }
  });
  return items;
}

interface IBookingEnquiryContextValue {
  snapshot: IBookingEnquirySnapshot;
  updateSnapshot: (snapshot: Partial<IBookingEnquirySnapshot>) => void;
  clearSnapshot: () => void;
}

export const DEFAULT_BOOKING_ENQUIRY_SNAPSHOT: IBookingEnquirySnapshot = {
  stage: "BASE_DETAILS"
};

const BookingEnquiryContext = createContext<IBookingEnquiryContextValue>({
  snapshot: DEFAULT_BOOKING_ENQUIRY_SNAPSHOT,
  updateSnapshot: () => undefined,
  clearSnapshot: () => undefined
});

export const useBookingEnquiryContext = () => useContext(BookingEnquiryContext);

interface IProps {
  children: React.ReactNode;
}

export function BookingEnquiryProvider(props: IProps) {
  const [snapshot, setSnapshot] = useState<IBookingEnquirySnapshot>(DEFAULT_BOOKING_ENQUIRY_SNAPSHOT);

  const updateSnapshot = useCallback((update: Partial<IBookingEnquirySnapshot>) => {
    setSnapshot((prev) => ({ ...prev, ...update }));
  }, []);

  const clearSnapshot = useCallback(() => {
    setSnapshot(DEFAULT_BOOKING_ENQUIRY_SNAPSHOT);
  }, []);

  return (
    <BookingEnquiryContext.Provider value={{ snapshot, updateSnapshot, clearSnapshot }}>
      {props.children}
    </BookingEnquiryContext.Provider>
  );
}
